/**
 * The approvals queue, for a manager.
 *
 * The other side of the at-the-till PIN. When a cashier cannot cancel or edit a
 * placed bill themselves, they send it here; this is where whoever may approve
 * one sees what is being asked and decides it — with their own PIN, on their own
 * screen, without having to walk to the till.
 *
 * WHY THE PIN IS ASKED FOR ONCE, AT THE TOP
 * -----------------------------------------
 * A manager clearing a run of requests should not retype four digits for each.
 * It is entered once and used for every decision made from this screen; the
 * server checks it on every one, so a PIN typed here and left is no more trust
 * than a manager standing at their own screen already is.
 *
 * WHY IT POLLS
 * ------------
 * Requests arrive while the screen is open — a cashier three rooms away pressing
 * "send to a manager". The queue has to fill on its own, so it re-reads every
 * few seconds, the same way the kitchen board does.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';

const POLL_MS = 5_000;

const KIND = {
  void: { label: 'Cancel', tone: 'text-bad' },
  revise: { label: 'Edit', tone: 'text-warn' },
  remove_item: { label: 'Remove', tone: 'text-warn' },
};

export default function ApprovalsPage() {
  const client = useQueryClient();
  const [pin, setPin] = useState('');

  const feed = useQuery({
    queryKey: ['pos', 'approvals'],
    queryFn: () => api.get('/pos/approvals'),
    refetchInterval: POLL_MS,
  });

  const decide = useMutation({
    mutationFn: ({ id, action, note }) =>
      api.post(`/pos/approvals/${id}/${action}`, { pin, ...(note ? { note } : {}) }),
    onSuccess: () => {
      // The decided one drops off the queue; the till's own poll picks up the
      // outcome. The cart/board elsewhere may have changed, so nudge those too.
      client.invalidateQueries({ queryKey: ['pos', 'approvals'] });
      client.invalidateQueries({ queryKey: ['pos', 'open-orders'] });
      client.invalidateQueries({ queryKey: ['kitchen'] });
    },
  });

  const requests = feed.data?.requests ?? [];
  const ready = pin.trim().length >= 4;

  if (feed.isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Spinner label="Loading requests" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl p-4">
      <header className="mb-4">
        <h1 className="font-semibold text-xl text-ink">Approvals</h1>
        <p className="mt-0.5 text-sm text-ink-2">
          Cancels and edits a cashier has sent up. Enter your PIN, then approve or turn down each one.
        </p>
      </header>

      <label className="mb-4 block">
        <span className="mb-1 block text-sm font-medium text-ink-2">Your PIN</span>
        <input
          aria-label="Your PIN"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={8}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          placeholder="••••"
          className="h-11 w-40 rounded-lg border border-rule bg-surface-3 px-3 text-sm tabular-nums text-ink placeholder:text-ink-3 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
        />
      </label>

      {decide.isError && (
        <p role="alert" className="mb-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {decide.error?.message ?? 'That did not work.'}
        </p>
      )}

      {requests.length === 0 ? (
        <p className="rounded-xl border border-rule bg-surface-3 py-16 text-center text-sm text-ink-3">
          Nothing waiting. Requests appear here on their own.
        </p>
      ) : (
        <ul className="space-y-2.5">
          {requests.map((r) => {
            const kind = KIND[r.action] ?? { label: r.action, tone: 'text-ink-2' };
            const busy = decide.isPending && decide.variables?.id === r.id;
            // The first detail is the order's context ("Dine in · Table 4"); the
            // rest are the itemised changes the manager is being asked to allow.
            const details = r.details ?? [];
            const context = details[0];
            const changes = details.slice(1);
            const hasBreakdown = context || changes.length > 0;
            return (
              <li key={r.id} className="rounded-xl border border-rule bg-surface p-3.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className={`text-sm font-semibold ${kind.tone}`}>{kind.label}</span>
                      <span className="tabular-nums text-sm text-ink">{r.orderNumber}</span>
                      {context && <span className="text-xs text-ink-3">{context}</span>}
                    </p>
                    {r.reason && <p className="mt-0.5 text-sm text-ink-2">“{r.reason}”</p>}
                    {changes.length > 0 && (
                      <ul className="mt-1.5 space-y-0.5">
                        {changes.map((line, i) => (
                          <li key={i} className="flex gap-1.5 text-sm text-ink">
                            <span aria-hidden="true" className="text-ink-3">
                              •
                            </span>
                            <span className="tabular-nums">{line}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* Older requests, made before the breakdown existed, still
                        show their one-line summary. */}
                    {!hasBreakdown && !r.reason && (
                      <p className="mt-0.5 text-sm text-ink-2">{r.summary}</p>
                    )}
                    <p className="mt-1.5 text-xs text-ink-3">
                      Asked by {r.requestedByName ?? 'a cashier'} · {ago(r.createdAt)}
                    </p>
                  </div>
                </div>

                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    busy={busy && decide.variables?.action === 'approve'}
                    disabled={!ready || decide.isPending}
                    onClick={() => decide.mutate({ id: r.id, action: 'approve' })}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    busy={busy && decide.variables?.action === 'reject'}
                    disabled={!ready || decide.isPending}
                    onClick={() => {
                      const note = window.prompt('Reason for turning it down? (optional)') ?? undefined;
                      // Cancelling the prompt (null → coerced to undefined above via ??) still
                      // sends a reject; only an explicit dismissal should stop it.
                      decide.mutate({ id: r.id, action: 'reject', note: note || undefined });
                    }}
                  >
                    Reject
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!ready && requests.length > 0 && (
        <p className="mt-3 text-xs text-ink-3">Enter your PIN above to approve or reject.</p>
      )}
    </div>
  );
}

/** A short "how long ago", from a stored timestamp. */
function ago(ts) {
  if (!ts) return 'just now';
  const d = new Date(ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z');
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (Number.isNaN(mins)) return '';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return `${hrs}h ago`;
}
